import * as yup from 'yup';
import { metastasisId } from './configs/consts';

const diseaseSchema = yup.object().shape({
  id: yup.string().nullable(),
  cancerLocationId: yup
    .string()
    .required('Cancer Location is required.')
    .meta({ section: 'diseases' }),
  cancerPathologyId: yup
    .string()
    .required('Disease Type is required.')
    .meta({ section: 'diseases' }),
  cancerPathologyOtherComments: yup.string().nullable().meta({ section: 'diseases' }),
  initialDiagnosisDate: yup
    .date()
    .nullable()
    .meta({ section: 'diseases' })
    .required('Initial Diagnosis Date is required.'),
  episodes: yup.lazy((map: any) => {
    const newSchema = yup.object(mapRules(map, episodeSchema));

    return newSchema;
  }),
});

const episodeSchema = yup.object().shape({
  isTargetEpisode: yup.boolean().required().meta({ section: 'episodes' }),
  areasOfMetastasisOtherComments: yup.string().nullable().meta({ section: 'episodes' }),
  areasOfRecurrenceOtherComments: yup.string().nullable().meta({ section: 'episodes' }),
  diagnosisStatus: yup
    .string()
    .required('Diagnosis Status is required.')
    .meta({ section: 'episodes' }),
  episodeDate: yup.date().required('Date is required.').nullable().meta({ section: 'episodes' }),
  cancerBehaviorId: yup
    .string()
    .nullable()
    .when('isTargetEpisode', {
      is: true,
      then: yup.string().trim().required('Behavior is required.'),
    })
    .meta({ section: 'episodes' }),
  cancerSettingId: yup
    .string()
    .nullable()
    .when('isTargetEpisode', {
      is: true,
      then: yup.string().trim().required('Setting is required.'),
    })
    .meta({ section: 'episodes' }),
  laterality: yup.string().nullable().meta({ section: 'episodes' }),
  recurrenceExtentId: yup.string().nullable().meta({ section: 'episodes' }),
  areasOfRecurrence: yup.array().of(yup.string()).nullable().meta({ section: 'episodes' }),
  areasOfMetastasis: yup
    .array()
    .of(yup.string())
    .nullable()
    .meta({ section: 'episodes' })
    .when(['cancerSettingId', 'diagnosisStatus'], {
      is: (cancerSettingId: string, diagnosisStatus: string) => {
        if (cancerSettingId === metastasisId && diagnosisStatus !== 'Recurrence') {
          return true;
        }
        return false;
      },
      then: yup
        .array()
        .of(yup.string())
        .min(1, 'At least 1 area of metastasis')
        .required('Areas of Metastasis is required.'),
    }),
  isSaved: yup.boolean().required(),
});

const mapRules = (map: any, rule: any) => {
  if (!map) return {};
  const keys = Object.keys(map);
  const reduction = keys.reduce((newMap, key) => ({ ...newMap, [key]: rule }), {});
  return reduction;
};

export default yup.object().shape({
  submitTo: yup.object().shape({
    highPriority: yup.boolean().nullable(),
    reasonsForRequest: yup.string().trim().required('Reason for request is required'),
    autoImmuneDisease: yup.boolean(),
    uncontrolledDiabetes: yup.boolean(),
    activeInfection: yup.boolean(),
    dvtpe: yup.boolean(),
    lowEjectionFraction: yup.boolean(),
    hepatitis: yup.boolean(),
    hiv: yup.boolean(),
    highQt: yup.boolean(),
    brainMetastasis: yup.boolean(),
  }),
  referringProvider: yup.object().shape({
    email: yup
      .string()
      .email('This must be a valid email')
      .required('Referring Provider Email is required'),
    name: yup.string().trim().required('Referring Provider Name is required'),
    phone: yup
      .string()
      .trim()
      .matches(/^\d{3}-?\d{3}-?\d{4}$/, 'Must be a valid phone number')
      .required('Referring Provider Phone is required'),
  }),
  patient: yup.object().shape({
    age: yup.number().typeError('Age is required').required('Age is required'),
    sexId: yup.string().trim().required('Sex is required'),
    biopsyCollectionSite: yup.string().trim().required('Biopsy Collection Site is required'),
    isTissueAvailable: yup.number().required('Is Tissue Available is required'),
    dateOfLastBiopsy: yup.date().nullable().required('Date of Last Biopsy is required'),
    pathologyText: yup.string().trim().required('Pathology Text is required'),
    pathologyStatus: yup.string().required('Pathology Status is required'),
    pathologyDate: yup.date().nullable(),
  }),
  patientHistory: yup.object().shape({
    chiefComplaint: yup.string().nullable(),
    medicalHistory: yup.string().trim().required(),
    familyHistory: yup.string().nullable(),
    socialHistory: yup.string().nullable(),
    menopausalStatus: yup.string().nullable(),
    breastOvarianCancerNear: yup.number().nullable(),
    miscellaneousCaseDetails: yup.string().nullable(),
  }),
  diseases: yup.lazy((map: any) => {
    const newSchema = yup.object(mapRules(map, diseaseSchema));

    return newSchema;
  }),
  noTreatmentHistory: yup.boolean().when('treatmentHistory', {
    is: (treatmentHistory: any) => !treatmentHistory?.length,
    then: yup.boolean().oneOf([true], 'A treatment history is required or check the no treatment history checkbox'),
  }),
  hasGivenConsent: yup.bool().oneOf([true], 'You must consent before submitting'),
});
